import {
  divideFloats,
  multiplyFloats,
  round,
  subtractFloats,
  toFixed,
} from "@shared/utils/numbers";
import {
  AuthUser,
  BasePlanDetails,
  EnvironmentType,
  EXTEND_SUBSCRIPTION_DURATIONS,
  FormattedPlanDetails,
  FormattedPlanDetailsMapping,
  PlanDetails,
  PromoCodePlansPricesType,
  PURCHASE_SUBSCRIPTION_TYPES,
  PurchaseSubscriptionType,
  SubscriptionDuration,
  SubscriptionPlanType,
} from "@shared/types";
import {
  getSubscriptionTypeDisplay,
  isWhitelabelSubscriptionType,
} from "@shared/utils/misc";
import {
  SUBSCRIPTION_STEPS,
  SubscriptionParams,
  SubscriptionStep,
} from "@shared/features/settings/subscription/types";
import moment, { Moment } from "moment";
import { dateIsLifetime, dateToMoment, formatMoment } from "@shared/utils/date";
import { mapKeys } from "lodash";
import { DURATION_TO_TIME_UNIT } from "@shared/features/settings/subscription/constants";

export const getWhitelabelPlanConflictError = (
  userPlanType?: SubscriptionPlanType,
  targetPlanType?: SubscriptionPlanType
): string | undefined => {
  if (
    isWhitelabelSubscriptionType(userPlanType) &&
    isWhitelabelSubscriptionType(targetPlanType) &&
    userPlanType !== targetPlanType
  ) {
    const targetPlan = getSubscriptionTypeDisplay(targetPlanType);
    const currentPlan = getSubscriptionTypeDisplay(userPlanType);
    return `You cannot purchase the ${targetPlan} plan while on the ${currentPlan} plan. If you wish to change plans, please contact support.`;
  }
};

export const getPlanPrice = (plan?: BasePlanDetails) => {
  return round((plan?.price || 0) - (plan?.discount || 0), 2);
};

export const getPlanDiscount = (
  plans: FormattedPlanDetailsMapping | undefined,
  subscriptionType: SubscriptionPlanType,
  duration: SubscriptionDuration,
  promoCodePrice?: number
): [number, number, number] => {
  if (duration === 30 || !plans) return [0, 0, 0];
  const baseMonthlyPrice = plans[subscriptionType][30]?.price || 0;
  const durationPrice =
    promoCodePrice ||
    subtractFloats(
      plans[subscriptionType][duration]?.price || 0,
      plans[subscriptionType][duration]?.discount || 0
    );
  const baseDurationPrice = duration
    ? multiplyFloats(divideFloats(duration, 30), baseMonthlyPrice)
    : 0;
  const amountDiscount = subtractFloats(baseDurationPrice, durationPrice);
  const discountPercent = baseDurationPrice
    ? round((amountDiscount / baseDurationPrice) * 100, 0)
    : 0;
  const roundedDiscount = toFixed(amountDiscount, 1, true);
  return [
    roundedDiscount,
    toFixed(discountPercent, 1, true),
    toFixed((360 / duration) * roundedDiscount, 1, true),
  ];
};

export const convertPriceToMonthlyPrice = (price: number, duration: number) => {
  return round(price / (duration / 30), 2);
};

export const getPlanPercentageDiscount = (
  plan: FormattedPlanDetails,
  duration: SubscriptionDuration
) => {
  if (plan[30] && plan[duration]?.discount) {
    const originalPrice = plan[30].price * (duration / 30);
    const diff =
      originalPrice -
      ((plan[duration]?.price || 0) - (plan[duration]?.discount || 0));
    return Math.round((diff / originalPrice) * 100);
  }
};

export const preprocessPlansData = (
  plans: PlanDetails[]
): FormattedPlanDetailsMapping | undefined => {
  if (!plans?.length) return;

  const getBasePlanDetails = (plan: PlanDetails) => ({
    discount: plan.discount,
    price: plan.price,
    subscription_days: plan.subscription_days,
    subscription_plan_id: plan.subscription_plan_id,
    additional_slot_price: plan.additional_slot_price,
  });

  return plans.reduce((acc, plan) => {
    if (plan.subscription_plan_type in acc) {
      acc[plan.subscription_plan_type][plan.subscription_days] =
        getBasePlanDetails(plan);
    } else {
      acc[plan.subscription_plan_type] = {
        subscription_type: plan.subscription_type,
        subscription_plan_type: plan.subscription_plan_type,
        subscription_features: plan.subscription_features,
        [plan.subscription_days]: getBasePlanDetails(plan),
      };
    }
    if (plan.allow_upgrade) {
      acc[plan.subscription_plan_type].upgradePlan = plan;
    }
    return acc;
  }, {} as FormattedPlanDetailsMapping);
};

export const validateQueryDuration = (duration: string) => {
  return EXTEND_SUBSCRIPTION_DURATIONS.includes(parseInt(duration));
};

const SUBSCRIPTION_PLAN_DISPLAY_TO_TYPE: Record<string, SubscriptionPlanType> =
  mapKeys(SubscriptionPlanType, (planType) =>
    getSubscriptionTypeDisplay(planType)
  );

export const getParsedQueryPlan = (queryPlan?: any) => {
  if (!queryPlan) return;
  let parsedPlan = queryPlan;
  if (!isNaN(queryPlan)) parsedPlan = parseInt(queryPlan);
  else if (queryPlan !== "Group") {
    parsedPlan = SUBSCRIPTION_PLAN_DISPLAY_TO_TYPE[queryPlan];
  }
  if (PURCHASE_SUBSCRIPTION_TYPES.includes(parsedPlan)) {
    return parsedPlan as PurchaseSubscriptionType;
  }
};

export const validateStep = (
  step: string,
  params: Partial<SubscriptionParams>,
  isUpgrade: boolean
): step is SubscriptionStep => {
  if (Object.values(SUBSCRIPTION_STEPS).includes(step as SubscriptionStep)) {
    if (step === SUBSCRIPTION_STEPS.DURATION) {
      return !!params.plan && (!!params.duration || isUpgrade);
    }
    return true;
  }
  return false;
};

export const getInitialPrice = (
  plan: FormattedPlanDetails | undefined,
  duration: number
) => {
  return toFixed((plan?.[30]?.price || 0) * (duration / 30), 2);
};

export const getInitialTotalPrice = (
  plan: FormattedPlanDetails | undefined,
  duration: number,
  isCryptoPayment?: boolean
) => {
  const initialPrice = getInitialPrice(plan, duration);
  return addFeeToPrice(initialPrice, isCryptoPayment);
};

export const addFeeToPrice = (price: number, isCryptoPayment?: boolean) => {
  const extraFee = round(isCryptoPayment ? 0.05 * price : 0, 2);
  return round(price + extraFee, 2);
};

export const getUserExpiration = (
  user: AuthUser | undefined,
  duration: number,
  isUpgrade?: boolean
) => {
  let userExpiration: number | Moment | undefined | null =
    user?.expiration_timestamp;
  if (
    !userExpiration ||
    dateIsLifetime(userExpiration, true) ||
    userExpiration < moment().unix()
  ) {
    userExpiration = moment().unix();
  }
  const momentUserExpiration = dateToMoment(userExpiration, true);
  if (!isUpgrade) {
    momentUserExpiration.add(duration, "days");
  }
  return formatMoment(momentUserExpiration);
};

export const getAppleGoogleExpirationDate = (
  user: AuthUser | undefined,
  duration: number,
  isUpgrade?: boolean
) => {
  let date = moment().unix();
  if (
    user?.expiration_timestamp &&
    !isUpgrade &&
    user?.isNativeSubscription()
  ) {
    date = user.expiration_timestamp;
  }
  const momentDate = dateToMoment(date, true);
  momentDate.add(1, DURATION_TO_TIME_UNIT[duration]);
  return formatMoment(momentDate);
};

export const getPlanErrorMessage = (
  plans?: FormattedPlanDetailsMapping,
  plan?: PurchaseSubscriptionType,
  showMobileAppWarning?: boolean,
  user?: AuthUser
) => {
  if (plans && plan && !(plan in plans))
    return "The plan you are trying to access is no longer available. Please choose a different plan or contact support.";
  if (showMobileAppWarning) {
    return `Your subscription was purchased through ${
      user?.isGooglePaySubscription() ? "Google Pay" : "Apple Pay"
    } and can only be managed through our mobile app. Please use the app to make changes to your subscription.`;
  }
};

export const getProductId = (
  environment: EnvironmentType,
  subscriptionType?: string,
  duration?: number,
  discount?: number,
  isGooglePay?: boolean
) => {
  const isProd = environment === EnvironmentType.PROD;
  let displayedSubscription = subscriptionType?.replace(" ", "-");
  displayedSubscription = isGooglePay
    ? displayedSubscription?.toLowerCase()
    : displayedSubscription?.toUpperCase();

  let productData = `${displayedSubscription}_${duration}`;
  if (discount && !isGooglePay) {
    return `${isProd ? "" : "TEST-"}${productData.replace(
      "_",
      "-"
    )}-${discount}`;
  }
  const prefix = isGooglePay ? "prod" : isProd ? "PROD" : "TEST";
  const suffix = isGooglePay ? "sub" : "SUB";
  return `${prefix}_${productData}_${suffix}`;
};

export const getPromoCodeRoundedDiscount = (
  plans: FormattedPlanDetailsMapping | undefined,
  plan: FormattedPlanDetails | undefined,
  duration: SubscriptionDuration,
  promoCodePrices?: PromoCodePlansPricesType
) => {
  if (!plan?.subscription_plan_type || !promoCodePrices) return;

  const [_, discountPercent] = getPlanDiscount(
    plans,
    plan.subscription_plan_type,
    duration,
    promoCodePrices[duration]?.recurring_price
  );
  return discountPercent ? Math.round(discountPercent / 5) * 5 : undefined;
};
